<template lang="pug">
v-dialog(v-model="show" fullscreen scrollable transition="dialog-bottom-transition")
  v-card
    v-card-title.py-0.pr-0
      v-layout(row align-center justify-space-between)
        v-layout(row align-center)
          v-avatar.mr-2.my-2(size="40" v-if="!$vuetify.breakpoint.xsOnly")
            v-icon.white--text.process-default-icon(:class="processDefinitionId.split(':')[0]")
          h3 {{ processDefinitionName }}
        v-btn(@click="show = false" color="blue darken-1" flat) {{ $tf("process.diagram.closeBtn") }}
    v-divider
    v-card-text.pa-0(style="height: 100%")
      .layout.row.wrap.bpmn-viewer(ref="bpmnViewer")
      .io-zoom-controls
        ul.io-control
          li
            v-btn.ma-0(icon @click="resetZoom" slot="activator" :ripple="false")
              v-icon fa fa-arrows-alt
        ul.io-control
          li
            v-btn.ma-0(icon @click="zoomIn" slot="activator" :ripple="false")
              v-icon fa fa-plus
          li
            hr
          li
            v-btn.ma-0(icon @click="zoomOut" slot="activator" :ripple="false")
              v-icon fa fa-minus
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import BpmnModdle from 'bpmn-moddle'
import BpmnNavigatedViewer from 'bpmn-js/lib/NavigatedViewer'
import MinimapModule from 'diagram-js-minimap'
import TouchModule from 'diagram-js/lib/navigation/touch'

export default {
  name: 'ProcessDiagram',

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    processDefinitionId: {
      type: String,
      default: null,
    },
    processDefinitionName: {
      type: String,
      default: null,
    },
    taskDefinitionKey: {
      type: String,
      default: null,
    },
    processVariables: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      show: false,
      viewer: null,
    }
  },

  computed: {
    ...mapGetters({
      task: 'task/task',
    }),
  },

  watch: {
    'value'(value) {
      if (value) {
        this.loadDiagram()
      }
    },

    'show'(value) {
      if (!value) {
        this.$emit('input', false)
      }
    },
  },

  methods: {
    ...mapActions({
      diagramXml: 'process/diagramXml',
      setSnackbar: 'tools/setSnackbar',
    }),

    diagramError(error) {
      console.error('BPMN diagram error', error)
      this.setSnackbar(this.$tf('process.diagram.error'))
    },

    loadDiagram() {
      this.diagramXml(this.processDefinitionId).then((response) => {
        const moddle = new BpmnModdle()
        moddle.fromXML(response.data.bpmn20Xml, (err, definition) => {
          if (err || !definition) {
            this.diagramError(err)
            return
          }
          definition.rootElements.forEach((rootElement) => {
            if (rootElement && rootElement.flowElements) {
              rootElement.flowElements.forEach((flowElement) => {
                if (flowElement.$type === 'bpmn:UserTask') {
                  const taskName = this.$tf(`${this.processDefinitionId.split(':')[0]}._process.task.${flowElement.id}`, flowElement.name)
                  flowElement.name = this.parseUserTaskName(taskName)
                }
              })
            }
          })
          // eslint-disable-next-line no-shadow
          moddle.toXML(definition, (err, xmlStrUpdated) => {
            if (err || !xmlStrUpdated) {
              this.diagramError(err)
              return
            }
            this.initDiagram(xmlStrUpdated)
          })
        })
      })
    },

    parseUserTaskName(userTaskName) {
      const expressions = userTaskName.match(/\$\{([^}]+)\}/g)
      if (expressions) {
        expressions.forEach((exp) => {
          // TypeA: ${S(variable).prop('name').......prop('name').stringValue()}
          const variableA = exp.match(/S\((.*?)\)/g)
          const propsA = exp.match(/\('(.*?)'\)/g)
          if (variableA && propsA) {
            userTaskName = userTaskName.replace(/\$\{S([^}]+)\}/g, this.getJsonVariableValue(variableA, propsA))
          } else {
            // TypeB: ${variable}
            const variableB = exp.match(/\$\{(.*?)\}/g)
            if (variableB) {
              const _var = variableB[0].replace('${', '').replace('}', '')
              userTaskName = userTaskName.replace(new RegExp(`\\$\\{${_var}\\}`, 'g'), this.getVariableValue(_var))
            }
          }
        })
      }
      return userTaskName
    },

    getJsonVariableValue(variable, props) {
      const vars = this.processVariables
      const _var = variable[0].replace('S(', '').replace(')', '')
      let rootVar = vars && vars[_var] && vars[_var].value ? vars[_var].value : null
      if (rootVar) {
        props.forEach((prop) => {
          const _prop = prop.replace(new RegExp('\'', 'g'), '').replace('(', '').replace(')', '')
          rootVar = rootVar[_prop]
        })
        return rootVar
      }
      return variable
    },

    getVariableValue(variable) {
      const vars = this.processVariables
      return vars && vars[variable] ? vars[variable].value : variable
    },

    initDiagram(bpmnXml) {
      // Диалог показываем ДО создания viewer. Пока он закрыт, контейнер имеет
      // нулевые размеры, diagram-js кэширует их в viewbox, и zoom('fit-viewport')
      // делит ширину на ноль — отсюда "non-finite value" в SVGMatrix.scale.
      this.show = true

      this.$nextTick(() => {
        if (!this.viewer) {
          this.viewer = new BpmnNavigatedViewer({
            container: this.$refs.bpmnViewer,
            width: '100%',
            height: '100%',
            additionalModules: [
              MinimapModule,
              TouchModule,
            ],
          })
        }

        this.viewer.importXML(bpmnXml, (err) => {
          if (err) {
            this.diagramError(err)
            return
          }
          const canvas = this.viewer.get('canvas')
          if (this.taskDefinitionKey) {
            canvas.addMarker(this.taskDefinitionKey, 'highlight-process-node')
          }
          this.fitViewport()
        })
      })
    },

    // viewer переиспользуется между открытиями диалога, а размер контейнера
    // diagram-js держит в кэше — без resized() расчёт пойдёт по старым размерам.
    // Пока идёт анимация открытия, контейнер может быть ещё нулевым: ждём кадр.
    fitViewport(attempt) {
      if (!this.viewer) {
        return
      }
      const canvas = this.viewer.get('canvas')
      canvas.resized()

      const size = canvas.getSize()
      if (!size.width || !size.height) {
        const next = (attempt || 0) + 1
        if (next <= 10) {
          window.requestAnimationFrame(() => this.fitViewport(next))
        }
        return
      }

      canvas.zoom('fit-viewport', 'auto')
    },

    resetZoom() {
      this.fitViewport()
    },

    zoomIn() {
      this.viewer.get('zoomScroll').zoom(1)
    },

    zoomOut() {
      this.viewer.get('zoomScroll').zoom(-1)
    },
  },

}
</script>

<style scoped lang="stylus">
</style>



// WEBPACK FOOTER //
// src/components/process/ProcessDiagram.vue